import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    IconButton,
    Typography,
    Box,
    Chip,
    Divider,
    Snackbar,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { crearPreexistencia, actualizarPreexistencia, eliminarPreexistencia } from '../../services/art/preexistenciaService';

const ModalPreexistencias = ({
    isOpen,
    onClose,
    clienteCuil,
    preexistencias = [],
    onPreexistenciaModificada
}) => {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        fechaReclamo: '',
        sede: '',
        descripcion: '',
        porcentaje: ''
    });
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [preexistenciaAEditar, setPreexistenciaAEditar] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [preexistenciaAEliminar, setPreexistenciaAEliminar] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const normalizarPorcentaje = (valor) => {
        if (valor === '' || valor === null || valor === undefined) return null;
        const normalizado = String(valor).replace(',', '.');
        const num = Number(normalizado);
        return Number.isNaN(num) ? null : num;
    };

    const validarFormulario = () => {
        const nuevosErrores = {};
        if (!form.fechaReclamo) nuevosErrores.fechaReclamo = 'La fecha es requerida';
        if (!form.sede) nuevosErrores.sede = 'La sede es requerida';
        if (!form.descripcion) nuevosErrores.descripcion = 'La descripción es requerida';
        if (form.porcentaje !== '') {
            const porcentajeNum = normalizarPorcentaje(form.porcentaje);
            if (porcentajeNum === null || porcentajeNum < 0 || porcentajeNum > 100) {
                nuevosErrores.porcentaje = 'El porcentaje debe estar entre 0 y 100';
            }
        }
        setErrors(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const resetFormulario = () => {
        setForm({ fechaReclamo: '', sede: '', descripcion: '', porcentaje: '' });
        setErrors({});
        setPreexistenciaAEditar(null);
    };

    const handleGuardar = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (!clienteCuil) return;
        if (!validarFormulario()) {
            setSnackbar({ open: true, message: 'Complete todos los campos requeridos.', severity: 'error' });
            return;
        }

        try {
            setLoading(true);
            const payload = {
                idCliente: clienteCuil,
                fechaReclamo: form.fechaReclamo,
                sede: form.sede,
                descripcion: form.descripcion,
                porcentaje: normalizarPorcentaje(form.porcentaje)
            };
            if (preexistenciaAEditar) {
                await actualizarPreexistencia(preexistenciaAEditar.idPreexistencia, payload);
            } else {
                await crearPreexistencia(payload);
            }
            if (typeof onPreexistenciaModificada === 'function') {
                await onPreexistenciaModificada();
            }
        } catch (err) {
            console.error('Error al guardar preexistencia:', err);
            setSnackbar({ open: true, message: 'No se pudo guardar la preexistencia.', severity: 'error' });
        } finally {
            setLoading(false);
            resetFormulario();
        }
    };

    const handleEditar = (p) => {
        setPreexistenciaAEditar(p);
        setForm({
            fechaReclamo: p.fechaReclamo || '',
            sede: p.sede || '',
            descripcion: p.descripcion || '',
            porcentaje: p.porcentaje ?? ''
        });
    };

    const handleConfirmarEliminar = (p) => {
        setPreexistenciaAEliminar(p);
        setConfirmOpen(true);
    };

    const handleEliminar = async () => {
        if (!preexistenciaAEliminar) return;
        try {
            setLoading(true);
            await eliminarPreexistencia(preexistenciaAEliminar.idPreexistencia);
            if (typeof onPreexistenciaModificada === 'function') {
                await onPreexistenciaModificada();
            }
        } catch (err) {
            console.error('Error al eliminar preexistencia:', err);
            setSnackbar({ open: true, message: 'No se pudo eliminar la preexistencia.', severity: 'error' });
        } finally {
            setLoading(false);
            setConfirmOpen(false);
            setPreexistenciaAEliminar(null);
        }
    };

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        position: 'relative'
                    }}
                >
                    Historial Médico (Preexistencias)
                    <IconButton
                        onClick={onClose}
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
                        aria-label="Cerrar"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ mt: 2 }}>
                    {preexistencias.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {preexistencias.map((p) => (
                                <Box
                                    key={p.idPreexistencia}
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2,
                                        p: 2,
                                        backgroundColor: '#f9f9f9',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 2
                                    }}
                                >
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Fecha: {p.fechaReclamo || 'N/A'}
                                            </Typography>
                                            {p.porcentaje !== null && p.porcentaje !== undefined && p.porcentaje !== '' && (
                                                <Chip
                                                    label={`% ${p.porcentaje}`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Sede: {p.sede || 'N/A'}
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                                            {p.descripcion || 'Sin descripción'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton size="small" color="primary" onClick={() => handleEditar(p)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleConfirmarEliminar(p)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Box component="form" onSubmit={handleGuardar} noValidate>
                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                            <TextField
                                label="Fecha de reclamo"
                                name="fechaReclamo"
                                type="date"
                                value={form.fechaReclamo}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.fechaReclamo}
                                helperText={errors.fechaReclamo}
                                required
                            />
                            <TextField
                                label="Sede"
                                name="sede"
                                type="text"
                                value={form.sede}
                                onChange={handleChange}
                                error={!!errors.sede}
                                helperText={errors.sede}
                                required
                            />
                            <TextField
                                label="Descripción"
                                name="descripcion"
                                type="text"
                                value={form.descripcion}
                                onChange={handleChange}
                                error={!!errors.descripcion}
                                helperText={errors.descripcion}
                                required
                            />
                            <TextField
                                label="Porcentaje"
                                name="porcentaje"
                                type="text"
                                inputMode="decimal"
                                value={form.porcentaje}
                                onChange={handleChange}
                                error={!!errors.porcentaje}
                                helperText={errors.porcentaje || 'Opcional (0-100). Podés usar coma o punto'}
                                inputProps={{ inputMode: 'decimal' }}
                            />
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button onClick={onClose} disabled={loading} sx={{ borderRadius: '20px' }}>
                        Cerrar
                    </Button>
                    {preexistenciaAEditar && (
                        <Button
                            variant="text"
                            color="error"
                            onClick={resetFormulario}
                            disabled={loading}
                            sx={{ borderRadius: '20px' }}
                        >
                            Cancelar edición
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleGuardar}
                        disabled={loading}
                        sx={{ borderRadius: '20px' }}
                    >
                        {loading ? 'Guardando...' : (preexistenciaAEditar ? 'Actualizar' : 'Agregar')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Confirmar eliminación
                </DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary">
                        ¿Está seguro de eliminar esta preexistencia?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button onClick={() => setConfirmOpen(false)} sx={{ borderRadius: '20px' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleEliminar}
                        disabled={loading}
                        sx={{ borderRadius: '20px' }}
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ModalPreexistencias;

